import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface Activity {
  id: number
  title: string
}

interface TimeEntry {
  id: number
  user_name: string | null
  user_id: number | null
  service_type: string | null
  service_item_type_id: number | null
  time_type: string
  started_at: string | null
  ended_at: string | null
  time_range: string | null
  admin_minutes: number
  travel_hours: number
  mileage: number
  mileage_on_job: number
  vehicle_allowance: string | null
  notes: string | null
  approval_notes: string | null
  created_at: string | null
  closed_at: string | null
  submitted: boolean
  approved_at: string | null
  approved_by: string | null
  approved: boolean
  paid_at: string | null
  paid_reference: string | null
  paid: boolean
  mileage_paid_at: string | null
  mileage_paid_reference: string | null
}

interface Props {
  activity: Activity
  time_entry: TimeEntry
  can_edit: boolean
  can_destroy: boolean
  can_submit: boolean
}

function formatMinutes(minutes: number): string {
  if (minutes <= 0) return "-"
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h > 0 && m > 0) return `${h}h ${m}m`
  if (h > 0) return `${h}h`
  return `${m}m`
}

export default function Show({
  activity,
  time_entry,
  can_edit,
  can_destroy,
  can_submit,
}: Props) {
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteReason, setDeleteReason] = useState("")

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Marketing Activities", href: "/marketing_activities" },
    { title: activity.title, href: `/marketing_activities/${activity.id}` },
    {
      title: "Time",
      href: `/marketing_activities/${activity.id}/time_entries`,
    },
    {
      title: "Details",
      href: `/marketing_activities/${activity.id}/time_entries/${time_entry.id}`,
    },
  ]

  const formatDate = (date: string | null) => {
    return (
      formatDateUtil(date, {
        month: "numeric",
        day: "numeric",
        year: "numeric",
      }) || "-"
    )
  }

  const handleDelete = () => {
    if (!deleteReason.trim()) return
    router.delete(
      `/marketing_activities/${activity.id}/time_entries/${time_entry.id}`,
      { data: { reason: deleteReason } },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Time Entry - ${activity.title}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            {time_entry.started_at
              ? formatDate(time_entry.started_at)
              : "Time Entry"}{" "}
            - {time_entry.service_type}
          </h1>
          <div className="flex space-x-2">
            {can_edit && (
              <Link
                href={`/marketing_activities/${activity.id}/time_entries/${time_entry.id}/edit`}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white"
              >
                Edit
              </Link>
            )}
            {can_destroy && (
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                className="bg-hpi-red hover:bg-hpi-red/80 rounded-md px-4 py-2 text-sm font-medium text-white"
              >
                Delete
              </button>
            )}
            {can_submit && (
              <button
                type="button"
                onClick={() => {
                  if (confirm("Submit this time entry for approval?")) {
                    router.post(
                      `/marketing_activities/${activity.id}/time_entries/${time_entry.id}/submit`,
                    )
                  }
                }}
                className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded-md px-4 py-2 text-sm font-medium text-white"
              >
                Submit for Approval
              </button>
            )}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">Time Details</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Employee</dt>
                <dd>{time_entry.user_name ?? "-"}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Type</dt>
                <dd>{time_entry.time_type}</dd>
              </div>
              {time_entry.time_type === "Actual" && time_entry.time_range && (
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Time</dt>
                  <dd>{time_entry.time_range}</dd>
                </div>
              )}
              {time_entry.time_type === "Block" && (
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Block Time</dt>
                  <dd>{formatMinutes(time_entry.admin_minutes)}</dd>
                </div>
              )}
              {time_entry.travel_hours > 0 && (
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Travel</dt>
                  <dd>{time_entry.travel_hours}h</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Service Type</dt>
                <dd>{time_entry.service_type ?? "-"}</dd>
              </div>
            </dl>
          </div>

          {time_entry.mileage > 0 && (
            <div className="rounded-lg bg-white p-6 shadow">
              <h2 className="mb-4 text-lg font-semibold">
                Mileage
                {time_entry.vehicle_allowance === "Yes" ? (
                  <span className="text-hpi-dark-green ml-2 text-sm font-normal">
                    Reimbursement Requested
                  </span>
                ) : (
                  <span className="text-hpi-red ml-2 text-sm font-normal">
                    Reimbursement not Requested
                  </span>
                )}
              </h2>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Total</dt>
                  <dd>{time_entry.mileage}</dd>
                </div>
                {time_entry.mileage_on_job > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-hpi-med-gray">OTJ</dt>
                    <dd>{time_entry.mileage_on_job}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          <div className="rounded-lg bg-white p-6 shadow md:col-span-2">
            <h2 className="mb-4 text-lg font-semibold">Workflow Status</h2>
            <div className="grid gap-4 md:grid-cols-4">
              <div>
                <h3 className="font-medium">Added</h3>
                <div className="text-hpi-med-gray mt-1 text-sm">
                  {formatDate(time_entry.created_at)}
                </div>
              </div>
              <div>
                <h3 className="font-medium">Submitted</h3>
                {time_entry.submitted ? (
                  <div className="text-hpi-med-gray mt-1 text-sm">
                    {formatDate(time_entry.closed_at)}
                  </div>
                ) : (
                  <div className="text-hpi-red mt-1 text-sm">Unsubmitted</div>
                )}
              </div>
              <div>
                <h3 className="font-medium">Approval</h3>
                {time_entry.approved_at ? (
                  <div className="mt-1 text-sm">
                    <div className="text-hpi-dark-green">Approved</div>
                    {time_entry.approved_by && (
                      <div className="text-hpi-med-gray">
                        by {time_entry.approved_by}
                      </div>
                    )}
                    <div className="text-hpi-med-gray">
                      {formatDate(time_entry.approved_at)}
                    </div>
                  </div>
                ) : (
                  <div className="text-hpi-med-gray mt-1 text-sm">
                    Pending Approval
                  </div>
                )}
              </div>
              <div>
                <h3 className="font-medium">Payment</h3>
                {time_entry.paid_at ? (
                  <div className="mt-1 text-sm">
                    <div className="text-hpi-dark-green">Paid</div>
                    {time_entry.paid_reference && (
                      <div className="text-hpi-med-gray">
                        Ref: {time_entry.paid_reference}
                      </div>
                    )}
                    <div className="text-hpi-med-gray">
                      {formatDate(time_entry.paid_at)}
                    </div>
                  </div>
                ) : (
                  <div className="text-hpi-med-gray mt-1 text-sm">
                    Not Yet Paid
                  </div>
                )}
              </div>
            </div>
          </div>

          {(time_entry.notes ?? time_entry.approval_notes) && (
            <div className="rounded-lg bg-white p-6 shadow md:col-span-2">
              <h2 className="mb-4 text-lg font-semibold">Explanation</h2>
              {time_entry.notes && (
                <p className="text-hpi-dark-gray whitespace-pre-wrap">
                  {time_entry.notes}
                </p>
              )}
              {time_entry.approval_notes && (
                <div className="text-hpi-red mt-4">
                  <span className="font-medium">Review Notes:</span>{" "}
                  {time_entry.approval_notes}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6">
          <Link
            href={`/marketing_activities/${activity.id}/time_entries`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Time List
          </Link>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-semibold">Delete Time Entry</h3>
            <label className="text-hpi-dark-gray mb-2 block text-sm font-medium">
              Why are you deleting this time entry?
            </label>
            <textarea
              className="border-hpi-light-gray focus:border-hpi-active-blue focus:ring-hpi-active-blue mb-4 w-full rounded-md border shadow-sm"
              rows={4}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              required
            />
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false)
                  setDeleteReason("")
                }}
                className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded-md border bg-white px-4 py-2 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={!deleteReason.trim()}
                className="bg-hpi-red hover:bg-hpi-red/80 rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
